import { motion } from "framer-motion";
import React, { useState } from "react";

type ButtonProps = {
  btnText: React.ReactNode;
  onClick?: () => void;
  className?: string;
  size?: "default" | "small";
  type?: "button" | "submit" | "reset";
  overrideTextColor?: boolean; // <- NEW
  /** Keeps the button in its "hovered" colours even when the pointer isn't over it — for a toggle/filter that is currently selected. */
  active?: boolean;
};

export default function ButtonElement({
  btnText,
  onClick,
  className = "",
  size = "default",
  type = "button",
  overrideTextColor = false, // <- Default behavior remains
  active = false,
}: ButtonProps) {
  const [hovered, setHovered] = useState(false);
  const highlighted = hovered || active;

  const animationConfig =
    size === "small"
      ? {
          scale: 1.08,
          transition: {
            type: "spring",
            stiffness: 220,
            damping: 24,
            duration: 0.4,
          },
        }
      : {
          scale: 1.03,
          transition: {
            type: "spring",
            stiffness: 250,
            damping: 26,
            duration: 0.2,
          },
        };

  return (
    <motion.button
      type={type}
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      whileTap={{ scale: 0.96 }}
      whileHover={{ scale: animationConfig.scale }}
      aria-pressed={active || undefined}
      animate={{
        backgroundColor: highlighted ? "#44b388" : "#165e6c",
        ...(overrideTextColor ? {} : { color: highlighted ? "#44b388" : "white" }), // <- Conditionally animate text color
      }}
      transition={animationConfig.transition}
      className={`relative px-6 py-2 mt-[1px] border border-[#44b388] overflow-hidden rounded-lg ${className}`}
    >
      <motion.div
        className="absolute inset-0 z-0"
        initial={{ x: "-101%" }}
        animate={{ x: highlighted ? "0%" : "-101%" }}
        transition={{ duration: animationConfig.transition.duration }}
        style={{
          backgroundColor: highlighted ? "#44b388" : "#165e6c",
        }}
      />
      <motion.div
        className="absolute bottom-0 left-0 h-[2px] bg-white origin-left"
        initial={{ width: "0%" }}
        animate={{ width: highlighted ? "100%" : "0%" }}
        transition={{
          duration: animationConfig.transition.duration,
          ease: "easeOut",
        }}
        style={{ zIndex: 10 }}
      />
      <span className={`relative z-10 font-semibold ${overrideTextColor ? "" : "text-white"}`}>
        {btnText}
      </span>
    </motion.button>
  );
}
